import React, { useState, useEffect } from 'react';
import { Button } from './ui';
import api from '../services/api';

export default function CommentSection({ taskId }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadComments();
  }, [taskId]);

  const loadComments = async () => {
    try {
      const { data } = await api.get(`/comments/task/${taskId}`);
      setComments(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      
      // Add basic fields as strings
      formData.append('task_id', String(taskId));
      formData.append('description', newComment);
      
      // Add files if any
      if (files.length > 0) {
        files.forEach(file => {
          formData.append('documents', file);
        });
      }

      // Let axios handle the Content-Type header automatically
      const response = await api.post('/comments', formData);
      console.log('Comment created successfully:', response.data);

      // Clear form and reload comments
      setNewComment('');
      setFiles([]);
      await loadComments();
    } catch (err) {
      console.error('Add comment error:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to add comment');
      }
    }
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download document');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    try {
      await api.delete(`/documents/${docId}`);
      // Reload comments to refresh the document list
      await loadComments();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete document');
    }
  };

  if (loading) return <div>Loading comments...</div>;

  return (
    <div className="comments-section">
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="comment-form">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          required
        />
        {/*both file upload and submit button should be in same row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="file-upload">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              id="file-input"
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
            />
            <label htmlFor="file-input" className="file-label">
              {files.length > 0 ? `${files.length} files selected` : 'Attach files'}
            </label>
            <div className="file-info">
              {files.length > 0 && (
                <small>Selected files: {files.map(f => f.name).join(', ')}</small>
              )}
            </div>
          </div>
          <div style={{ float: 'right', marginLeft: '8px' }}><Button type="submit">Add Comment</Button></div>
        </div>
      </form>

      <div className="comments-list">
        {comments.map(comment => (
          <div key={comment.id} className="comment">
            <div className="comment-header">
              <span className="author">{comment.created_by_user?.firstName} {comment.created_by_user?.lastName}</span>
              <span className="date">{new Date(comment.created_at).toLocaleString()}</span>
            </div>
            <div className="comment-content">{comment.description}</div>
            {comment.documents?.length > 0 && (
              <div className="documents">
                <h4>Attachments:</h4>
                <div className="document-list">
                  {comment.documents.map(doc => (
                    <div key={doc.id} className="document-item">
                      <span className="document-name">{doc.name}</span>
                      <div>
                        <Button
                          variant="secondary"
                          onClick={() => downloadDocument(doc)}
                          style={{ marginRight: '8px' }}
                        >
                          Download
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => deleteDocument(doc.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <style jsx>{`
        .comments-section {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .comment-form {
          margin-bottom: 20px;
        }

        .comment-form textarea {
          width: 100%;
          min-height: 100px;
          padding: 10px;
          margin-bottom: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          resize: vertical;
        }

        .file-upload {
          margin-bottom: 10px;
        }

        .file-upload input[type="file"] {
          display: none;
        }

        .file-label {
          display: inline-block;
          padding: 8px 16px;
          background-color: #f4f5f7;
          color: #42526e;
          border-radius: 4px;
          cursor: pointer;
        }

        .file-label:hover {
          background-color: #ebecf0;
        }

        .comments-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .comment {
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 16px;
        }

        .comment-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .author {
          font-weight: 500;
          color: #203c58;
        }

        .date {
          color: #6b778c;
        }

        .comment-content {
          margin-bottom: 12px;
          white-space: pre-wrap;
          color: #203c58;
        }

        .documents {
          border-top: 1px solid #eee;
          padding-top: 12px;
          margin-top: 12px;
        }

        .documents h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
          color: #42526e;
        }

        .document-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .document-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px;
          background-color: #fff;
          border-radius: 4px;
        }

        .document-name {
          font-size: 14px;
          color: #42526e;
        }

        .error-message {
          color: #de350b;
          background-color: #ffebe6;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 16px;
        }
      `}</style>
    </div>
  );
}